import { useEffect } from "react";
import { MiBox, MiList } from "@miplatform/components";
import { HTTP_STATUSES, PAGINATION } from "@miplatform/constants";
import { formatDate } from "@miplatform/utils";
import { ManageMilestonesModal } from "./ManageMilestonesModal";
import { useMilestonesViewModel } from "./useMilestonesViewModel";

/**
 * A tab component for displaying and managing milestones associated with a build template.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.buildTemplate - The build template object containing milestone data
 * @param {boolean} props.openAddModal - Controls the visibility of the milestones management modal
 * @param {Function} props.setOpenAddModal - Callback to control the add modal visibility state
 * @param {Function} props.onRefresh - Callback function to trigger data refresh
 * @returns {JSX.Element} The rendered milestones tab component
 *
 * @example
 * <MilestonesTab
 *   buildTemplate={selectedBuildTemplate}
 *   openAddModal={isModalOpen}
 *   setOpenAddModal={setModalOpen}
 *   onRefresh={handleRefreshData}
 * />
 *
 * @see {@link ManageMilestonesModal} for the modal component used to edit milestones
 * @see {@link useMilestonesViewModel} for the view model handling business logic
 */
export const MilestonesTab = ({
  buildTemplate,
  openAddModal,
  setOpenAddModal,
  onRefresh,
}) => {
  const { isLoading, milestones, getMilestones, updateBuildTemplate } =
    useMilestonesViewModel({ buildTemplateId: buildTemplate.id });

  const templateFieldset = [
    {
      field: "name",
      headerName: "Name",
      headerAlign: "left",
      groupable: false,
      flex: 1,
    },
    {
      field: "abbreviation",
      headerName: "Abbreviation",
      flex: 1,
    },
    {
      field: "createdBy",
      headerName: "Created By",
      flex: 1,
    },
    {
      field: "createdOn",
      headerName: "Created Date",
      renderCell: (p) => formatDate(p.row.createdOn, "DD MMMM YYYY"),
      flex: 1,
    },
    {
      field: "modifiedOn",
      headerName: "Last Updated",
      renderCell: (p) => formatDate(p.row.modifiedOn, "DD MMMM YYYY"),
      flex: 1,
    },
  ];

  const onModalClose = () => setOpenAddModal(false);

  const onEdit = async (data) => {
    onModalClose();
    const response = await updateBuildTemplate(data);
    if (response.status === HTTP_STATUSES.OK) {
      onRefresh();
    }
  };

  useEffect(() => {
    getMilestones();
  }, []);

  return (
    <>
      <MiBox height="100%">
        <MiList
          columns={templateFieldset}
          data={buildTemplate.milestones || []}
          paginationMode={PAGINATION.CLIENT}
          loading={isLoading}
          disableRowSelectionOnClick
          data-testid="milestones-table"
        />
      </MiBox>
      <ManageMilestonesModal
        open={openAddModal}
        onModalClose={onModalClose}
        buildTemplate={buildTemplate}
        milestones={milestones}
        onSubmit={onEdit}
        loading={isLoading}
      />
    </>
  );
};
